import { Capacitor } from '@capacitor/core';
import { BiometricAuth } from '@aparajita/capacitor-biometric-auth';
import { ROLES, STORAGE_KEYS } from '@/constants';
import { secureStorage } from '@/mobile/services/secureStorage.service';
import { preferenceStorage } from '@/utils/storage';
import { useAuthStore } from '@/store/authStore';
import { useSuperAdminAuthStore } from '@/store/superAdminAuthStore';
import { AuthService } from '@/services/authService';
import { getErrorMessage, homePathAfterLogin, isMarketingUser } from '@/utils/helpers';

export type BiometricStatus =
  | 'BIOMETRIC_AVAILABLE'
  | 'BIOMETRIC_NOT_ENROLLED'
  | 'BIOMETRIC_LOCKED_OUT'
  | 'BIOMETRIC_TEMPORARILY_UNAVAILABLE'
  | 'BIOMETRIC_UNAVAILABLE'
  | 'DEVICE_CREDENTIAL_AVAILABLE'
  | 'NOT_NATIVE'
  | 'UNKNOWN_ERROR';

export interface BiometricAvailability {
  available: boolean;
  status: BiometricStatus;
  reason?: string;
  biometryType?: string;
  hasDeviceSecurity?: boolean;
}

export interface BiometricCredential {
  userId: string;
  identifier: string;
  method: 'email' | 'mobile';
  refreshToken: string;
  scope?: 'PLATFORM' | 'SOCIETY' | 'MARKETING';
  societyName?: string;
  fullName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BiometricUnlockResult {
  success: boolean;
  cancelled?: boolean;
  sessionRevoked?: boolean;
  errorMessage?: string;
  destinationPath?: string;
}

export async function checkBiometricAvailability(): Promise<BiometricAvailability> {
  if (!Capacitor.isNativePlatform()) {
    return {
      available: false,
      status: 'NOT_NATIVE',
      reason: 'Biometric login is available only on the mobile app.',
    };
  }

  try {
    const result = await BiometricAuth.checkBiometry();
    const code = (result.code || '').toLowerCase();

    // 1. Device has strong or weak biometry enrolled and ready
    if (result.isAvailable || result.strongBiometryIsAvailable) {
      return {
        available: true,
        status: 'BIOMETRIC_AVAILABLE',
        hasDeviceSecurity: Boolean(result.deviceIsSecure),
        biometryType: String(result.biometryType ?? 'fingerprint'),
      };
    }

    // 2. Specific biometric error conditions
    if (code.includes('notenrolled') || code.includes('biometrynotenrolled') || code.includes('no_biometrics')) {
      return {
        available: false,
        status: 'BIOMETRIC_NOT_ENROLLED',
        reason: 'No biometric enrolled on this device. Please set up fingerprint or face unlock in Android Settings.',
        hasDeviceSecurity: Boolean(result.deviceIsSecure),
      };
    }

    if (code.includes('lockout')) {
      return {
        available: false,
        status: 'BIOMETRIC_LOCKED_OUT',
        reason: 'Biometric authentication is temporarily locked. Use your password.',
        hasDeviceSecurity: Boolean(result.deviceIsSecure),
      };
    }

    // 3. Fallback: device lock screen (PIN / pattern / passcode) configured
    if (result.deviceIsSecure) {
      return {
        available: true,
        status: 'DEVICE_CREDENTIAL_AVAILABLE',
        hasDeviceSecurity: true,
        biometryType: 'device_credential',
      };
    }

    return {
      available: false,
      status: 'BIOMETRIC_UNAVAILABLE',
      reason: result.reason || 'Biometric authentication unavailable on this device.',
    };
  } catch (error: any) {
    console.warn('[BiometricService] checkBiometry error:', error);
    return {
      available: false,
      status: 'UNKNOWN_ERROR',
      reason: error?.message || 'Unable to check biometric support on this device.',
    };
  }
}

export async function authenticateWithBiometrics(
  reason = 'Verify your identity to sign in to ApnaBlock',
): Promise<{ success: boolean; errorCode?: string; errorMessage?: string }> {
  if (!Capacitor.isNativePlatform()) {
    return { success: false, errorMessage: 'Biometric authentication plugin not available.' };
  }

  try {
    await BiometricAuth.authenticate({
      reason,
      androidTitle: 'Sign in to ApnaBlock',
      androidSubtitle: reason,
      cancelTitle: 'Cancel',
      allowDeviceCredential: true,
    });
    return { success: true };
  } catch (err: any) {
    const code = String(err?.code || err?.message || 'authenticationFailed');
    console.warn('[BiometricService] Authentication rejected:', code);
    return {
      success: false,
      errorCode: code,
      errorMessage: err?.message || 'Biometric authentication cancelled or failed.',
    };
  }
}

export async function isBiometricLoginEnabled(): Promise<boolean> {
  const value = await preferenceStorage.getItem(STORAGE_KEYS.biometricEnabled);
  return value === 'true';
}

export async function setBiometricLoginEnabled(enabled: boolean): Promise<void> {
  await preferenceStorage.setItem(
    STORAGE_KEYS.biometricEnabled,
    enabled ? 'true' : 'false',
  );
}

export async function saveBiometricCredentials(payload: {
  userId: string;
  identifier: string;
  method: 'email' | 'mobile';
  refreshToken: string;
  scope?: 'PLATFORM' | 'SOCIETY' | 'MARKETING';
  societyName?: string;
  fullName?: string;
}): Promise<void> {
  const credentialRecord: BiometricCredential = {
    ...payload,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  await secureStorage.setItem(
    STORAGE_KEYS.biometricCredentials,
    JSON.stringify(credentialRecord),
  );
  await setBiometricLoginEnabled(true);
}

export async function updateBiometricRefreshToken(newRefreshToken: string): Promise<void> {
  const current = await getBiometricCredentials();
  if (!current) return;

  const updated: BiometricCredential = {
    ...current,
    refreshToken: newRefreshToken,
    updatedAt: new Date().toISOString(),
  };

  await secureStorage.setItem(
    STORAGE_KEYS.biometricCredentials,
    JSON.stringify(updated),
  );
}

export async function getBiometricCredentials(): Promise<BiometricCredential | null> {
  const secureRaw = await secureStorage.getItem(STORAGE_KEYS.biometricCredentials);
  if (!secureRaw) return null;

  try {
    const parsed = JSON.parse(secureRaw) as BiometricCredential;
    if (parsed && parsed.refreshToken) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

export async function hasBiometricCredential(): Promise<boolean> {
  const creds = await getBiometricCredentials();
  return Boolean(creds && creds.refreshToken);
}

export async function clearBiometricCredentials(): Promise<void> {
  await secureStorage.removeItem(STORAGE_KEYS.biometricCredentials);
  await preferenceStorage.removeItem(STORAGE_KEYS.biometricCredentials);
  await setBiometricLoginEnabled(false);
}

/**
 * Bootstrap authentication lifecycle on application launch and resume.
 * Ensures that if biometric login is configured, persisted sessions are locked
 * until verified by device biometrics.
 */
export async function bootstrapAuthLifecycle(): Promise<void> {
  const authStore = useAuthStore.getState();

  if (!Capacitor.isNativePlatform()) {
    if (authStore.accessToken && authStore.user) {
      authStore.setBiometricLocked(false);
      authStore.setAuthState('AUTHENTICATED');
    } else {
      authStore.setBiometricLocked(false);
      authStore.setAuthState('UNAUTHENTICATED');
    }
    authStore.setHydrated(true);
    return;
  }

  try {
    const bioEnabled = await isBiometricLoginEnabled();
    const hasCreds = await hasBiometricCredential();

    if (bioEnabled && hasCreds) {
      authStore.setBiometricLocked(true);
      authStore.setAuthState('BIOMETRIC_REQUIRED');
    } else if (authStore.accessToken && authStore.user) {
      authStore.setBiometricLocked(false);
      authStore.setAuthState('AUTHENTICATED');
    } else {
      authStore.setBiometricLocked(false);
      authStore.setAuthState('UNAUTHENTICATED');
    }
  } catch (err) {
    console.warn('[BiometricLifecycle] Bootstrap error:', err);
    if (authStore.accessToken && authStore.user) {
      authStore.setBiometricLocked(false);
      authStore.setAuthState('AUTHENTICATED');
    } else {
      authStore.setBiometricLocked(false);
      authStore.setAuthState('UNAUTHENTICATED');
    }
  } finally {
    authStore.setHydrated(true);
  }
}

/**
 * Performs full biometric session unlocking:
 * 1. Native biometric prompt
 * 2. Unlocks securely encrypted refresh token
 * 3. Validates and rotates refresh token with backend
 * 4. Refreshes backend user profile, tenant, permissions, and scope
 * 5. Updates stores and returns target destination path
 */
export async function unlockSessionWithBiometrics(
  reason = 'Verify your identity to sign in to ApnaBlock',
): Promise<BiometricUnlockResult> {
  if (!Capacitor.isNativePlatform()) {
    return {
      success: false,
      errorMessage: 'Biometric login is available only on the mobile app.',
    };
  }

  const creds = await getBiometricCredentials();
  if (!creds || !creds.refreshToken) {
    return {
      success: false,
      errorMessage: 'Please sign in with your password to enable biometric login.',
    };
  }

  const authRes = await authenticateWithBiometrics(reason);
  if (!authRes.success) {
    const isCancelled =
      Boolean(authRes.errorCode?.includes('userCancel')) ||
      Boolean(authRes.errorCode?.includes('appCancel')) ||
      Boolean(authRes.errorCode?.includes('13')) ||
      Boolean(authRes.errorCode?.includes('10'));

    return {
      success: false,
      cancelled: isCancelled,
      errorMessage: authRes.errorMessage || 'Biometric authentication cancelled or failed.',
    };
  }

  try {
    // 1. Refresh session via backend API using securely stored refresh token
    const refreshResult = await AuthService.refresh(creds.refreshToken);
    const { accessToken, refreshToken: newRefreshToken } = refreshResult.data;

    // 2. Rotate refresh token in secure storage if new one issued
    if (newRefreshToken) {
      await updateBiometricRefreshToken(newRefreshToken);
    }

    useAuthStore.getState().setTokens(accessToken, newRefreshToken || creds.refreshToken);

    // 3. Fetch latest authoritative user profile and permissions
    const meResult = await AuthService.me();
    const meUser = meResult.data;

    const isMarketing = isMarketingUser(meUser);
    const isSuperAdmin = !isMarketing && Boolean(
      meUser?.roles?.includes(ROLES.SUPER_ADMIN) ||
      (meUser as any)?.role === 'SUPER_ADMIN' ||
      (meUser as any)?.scope === 'PLATFORM',
    );

    useAuthStore.getState().setSession({
      user: meUser,
      accessToken,
      refreshToken: newRefreshToken || creds.refreshToken,
      tenant: (meUser as any)?.tenant || null,
      permissions: (meUser as any)?.permissions || meUser.permissions || [],
      rememberMe: true,
    });

    let destination = '/dashboard';
    if (isMarketing) {
      useSuperAdminAuthStore.getState().clearPlatformAuth();
      useSuperAdminAuthStore.getState().clearImpersonation();
      destination = '/marketing';
    } else if (isSuperAdmin) {
      useSuperAdminAuthStore.getState().setPlatformAuth(accessToken, {
        _id: meUser.id || (meUser as any)._id || 'super-admin',
        firstName: meUser.firstName || 'Super',
        lastName: meUser.lastName || 'Admin',
        email: meUser.email,
        role: 'SUPER_ADMIN',
        isActive: true,
        createdAt: new Date().toISOString(),
      });
      useAuthStore.getState().setTenant(null);
      destination = '/super-admin';
    } else {
      useSuperAdminAuthStore.getState().clearPlatformAuth();
      useSuperAdminAuthStore.getState().clearImpersonation();
      destination = homePathAfterLogin(meUser);
    }

    useAuthStore.getState().setBiometricLocked(false);
    useAuthStore.getState().setAuthState('AUTHENTICATED');

    return {
      success: true,
      destinationPath: destination,
    };
  } catch (error: any) {
    console.warn('[BiometricUnlock] Backend session refresh failed:', error);
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
      // Session revoked or expired on backend: purge credentials
      await clearBiometricCredentials();
      useAuthStore.getState().logout();
      useSuperAdminAuthStore.getState().clearPlatformAuth();
      useSuperAdminAuthStore.getState().clearImpersonation();
      return {
        success: false,
        sessionRevoked: true,
        errorMessage: 'Session expired or revoked. Please sign in with your password.',
      };
    }

    return {
      success: false,
      errorMessage: getErrorMessage(error, 'Biometric login failed. Please use password.'),
    };
  }
}
